import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import config from 'config';
import { useAmplitudeExperiment } from './ampli';
import { selectUser } from '../reducers/user.selectors';
import {
  SEARCH_EXPERIMENT_DEMOTE_READ_ONLY,
  SEARCH_EXPERIMENT_DEEP_LINKING,
} from '../actions/search.actions';
import type { SearchExperiment } from '@kimuno/api-sdk/lib/types/requests/Common';

const useSearchExperiments = () => {
  const user = useSelector(selectUser);

  const {
    isReady: isDemoteReadOnlyReady,
    isEnabled: isDemoteReadOnlyEnabled,
    variant: demoteReadOnlyVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.demoteReadOnly);
  const {
    isReady: isBestMatchDeepLinkReady,
    isEnabled: isBestMatchDeepLinkEnabled,
    variant: bestMatchDeepLinkVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.bestMatchDeepLink);
  const {
    isReady: isRelatedTopicsReady,
    isEnabled: isRelatedTopicsEnabled,
    variant: relatedTopicsVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.relatedTopics);
  const {
    isReady: isUseAiFiltersReady,
    variant: useAiFiltersVariant
  } = useAmplitudeExperiment(user, config.amplitudeExperiments.useAiFilters);

  const searchExperiments = useMemo(() => {
    const experiments: SearchExperiment[] = [];
    if (isDemoteReadOnlyReady && demoteReadOnlyVariant === 'treatment') {
      experiments.push(SEARCH_EXPERIMENT_DEMOTE_READ_ONLY);
    }
    if (isBestMatchDeepLinkReady && bestMatchDeepLinkVariant === 'on') {
      experiments.push(SEARCH_EXPERIMENT_DEEP_LINKING);
    }
    return experiments.length > 0 ? experiments : undefined;
  }, [
    isDemoteReadOnlyReady, demoteReadOnlyVariant,
    isBestMatchDeepLinkReady, bestMatchDeepLinkVariant,
  ]);

  const { experimentName, experimentVariant } = useMemo(() => {
    if (isDemoteReadOnlyEnabled) {
      return { experimentName: config.amplitudeExperiments.demoteReadOnly, experimentVariant: demoteReadOnlyVariant };
    }
    if (isBestMatchDeepLinkEnabled) {
      return {
        experimentName: config.amplitudeExperiments.bestMatchDeepLink,
        experimentVariant: bestMatchDeepLinkVariant
      };
    }
    if (isRelatedTopicsEnabled) {
      return {
        experimentName: config.amplitudeExperiments.relatedTopics,
        experimentVariant: relatedTopicsVariant
      };
    }
    return { experimentName: '', experimentVariant: '' };
  }, [
    isDemoteReadOnlyEnabled, demoteReadOnlyVariant,
    isBestMatchDeepLinkEnabled, bestMatchDeepLinkVariant,
    isRelatedTopicsEnabled, relatedTopicsVariant,
  ]);

  // SEAR-460: boolean search param (not an experiment selector), so it is NOT
  // pushed into `searchExperiments`; it rides `SearchOptions.useAiFilters`.
  const useAiFilters = isUseAiFiltersReady && useAiFiltersVariant === 'treatment';

  const isReady = isDemoteReadOnlyReady
    && isBestMatchDeepLinkReady
    && isRelatedTopicsReady
    && isUseAiFiltersReady;

  return {
    isReady,
    isDemoteReadOnlyReady,
    isBestMatchDeepLinkReady,
    isRelatedTopicsReady,
    isUseAiFiltersReady,
    relatedTopicsVariant,
    useAiFilters,
    searchExperiments,
    experimentName,
    experimentVariant,
  };
};

export default useSearchExperiments;
